import React, { Fragment, useCallback, useEffect, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { Spinner, IconIllustrativeInfo, IconWarningFill } from '@blocket/blocket-ui'
import { tracking3dPartyAdClick, trackingWarningModalReadMoreBox } from 'tracking/chat'
import { useIntersectionObserver } from 'customHooks/useIntersectionObserver'
import { setCarouselImages } from 'reduxBits/ui/actions'

import Overlay from 'components/pagesContent/Chat/components/shared/Overlay'
import Image from './MessageTypes/Image'
import Admin from './MessageTypes/Admin'
import File from './CustomTypes/File'
import Location from './CustomTypes/Location'
import Extension from './CustomTypes/Extension'

import {
  ListWrapper,
  List,
  ListItem,
  ChatBubbleWrapper,
  ChatBubble,
  ChatBubbleIcon,
  ChatText,
  MetaData,
  TimeIndicator,
  TypingIndicator,
  DotIndicator,
  SpinnerWrapper,
  TopListInfo,
  MessageWarningLink,
  MessageWarningBoxText,
} from '../style.js'
import { getFormattedDate } from '../../../utils/moment'
import { supportedMimeTypesToShow } from '../../../constants/supportedFileTypes'
import ChannelInfoMessage from './ChannelInfoMessage'
import ChannelGdprMessage from './ChannelGdprMessage'
import ChannelTipMessage from './ChannelTipMessage'
import { channelOlderThanOneYear } from '../../../utils/channelAge'

const failedMessageText = 'Inte levererat'

const setReadStatus = (sendingStatus) => {
  switch (sendingStatus) {
    case 'succeeded':
      return 'Levererat'
    case 'pending':
      return 'Skickar'
    case 'failed':
      return failedMessageText
    default:
      return ''
  }
}

export const ChannelView = ({
  messages,
  listForwardRef,
  currentUserId,
  typing,
  isMobile,
  recipientReadLastMessage,
  selectedChannel,
  tip,
  messageListQuery,
  paginateMessages,
  recipientName,
  overlayActive,
  advertiserType,
}) => {
  const [showInfoMessage, setShowInfoMessage] = useState({})
  const [channelImages, setChannelImages] = useState([])
  const { images: carouselImages } = useSelector((state) => state.ui.carousel)
  const loadMoreRef = useRef()
  const dispatch = useDispatch()

  const observerCallBack = useCallback(() => {
    paginateMessages()
  }, [paginateMessages])

  const getImageIndex = ({ url }) => channelImages?.findIndex((image) => image.src === url)

  useIntersectionObserver(loadMoreRef, observerCallBack, { threshold: 1.0 })

  const messageList = messages.map((msg) => {
    const {
      sender,
      createdAt,
      customType,
      messageId,
      url,
      data,
      type,
      message,
      messageType,
      silent,
      containsExternalContactInfo,
      name,
      sendingStatus,
    } = msg
    const messageKey = createdAt + messageId
    const isMe = sender?.userId === currentUserId
    const date = getFormattedDate(createdAt, true)
    const isLastMessage = messageId === selectedChannel?.lastMessage?.messageId
    const isImage = customType === 'V1.IMG'
    const messageFailed = sendingStatus === 'failed'
    const showReadReceipt = (isMe && isLastMessage) || messageFailed

    if (!isMe && silent) {
      return null
    }

    if (type && !supportedMimeTypesToShow.includes(type)) {
      return (
        <ListItem isMe={isMe} key={messageKey} isImage={isImage} isMobile={isMobile}>
          <TimeIndicator isMe={isMe}>{date}</TimeIndicator>
          <ChatBubble isMe={isMe}>
            <ChatText isMe={isMe}>Fil kan inte hämtas</ChatText>
          </ChatBubble>
        </ListItem>
      )
    }

    if (customType === 'V1.EXTENSION') {
      return (
        <Extension
          key={messageKey}
          jsonData={data}
          currentUserId={currentUserId}
          date={date}
          isMobile={isMobile}
          isMe={isMe}
          selectedChannel={selectedChannel}
        />
      )
    }

    // currently not used
    if (messageType === 'admin') {
      return <Admin msg={msg} date={date} isImage={isImage} isMobile={isMobile} />
    }

    return (
      <Fragment key={messageKey}>
        <ListItem
          isMe={isMe}
          isImage={isImage}
          isMobile={isMobile}
          containsExternalContactInfo={containsExternalContactInfo}
        >
          <TimeIndicator isMe={isMe}>{date}</TimeIndicator>
          {isImage ? (
            <Image
              msg={msg}
              isMe={isMe}
              recipientName={recipientName}
              alt=""
              onImageClick={({ url }) =>
                dispatch(
                  setCarouselImages({ images: channelImages, activeIndex: getImageIndex({ url }) }),
                )
              }
            />
          ) : (
            <ChatBubbleWrapper>
              <ChatBubble isMe={isMe}>
                {customType === 'V1.TXT' && <ChatText isMe={isMe}>{message}</ChatText>}
                {customType === 'V1.LOCATION' && <Location isMe={isMe} jsonData={data} />}
                {customType === 'V1.FILE' && <File isMe={isMe} url={url} name={name} />}
              </ChatBubble>
              {messageFailed && (
                <ChatBubbleIcon title={failedMessageText}>
                  <IconWarningFill viewBox="0 0 32 32" height={20} width={20} color="#FF7300" />
                </ChatBubbleIcon>
              )}
            </ChatBubbleWrapper>
          )}
          {showReadReceipt && (
            <MetaData isMe={isMe}>
              {(recipientReadLastMessage && !messageFailed && 'Läst') ||
                setReadStatus(sendingStatus)}
            </MetaData>
          )}
        </ListItem>
        {containsExternalContactInfo && (
          <li>
            <MessageWarningLink
              href="https://www.blocket.se/om/sakerhet/handla-tryggt"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackingWarningModalReadMoreBox(selectedChannel.url)}
            >
              <IconIllustrativeInfo width={24} height={24} style={{ minWidth: '24px' }} />
              <MessageWarningBoxText>
                Om du flyttar konversationen utanför Blocket ökar risken för bedrägeriförsök. Var
                försiktig!
              </MessageWarningBoxText>
            </MessageWarningLink>
          </li>
        )}
      </Fragment>
    )
  })

  const showGdprMessage = !messageListQuery?.hasNext && !messageListQuery?.isLoading

  const handleTipOnClick = useCallback(() => {
    if (tip) {
      tracking3dPartyAdClick({ selectedChannel, tip })
    }
  }, [selectedChannel, tip])

  useEffect(() => {
    if (showInfoMessage.channel !== selectedChannel.url) {
      const value = channelOlderThanOneYear(selectedChannel)
      setShowInfoMessage({ value, channel: selectedChannel.url })
    }
  }, [showInfoMessage, selectedChannel])

  useEffect(() => {
    if (messages.length) {
      setChannelImages(
        messages
          .filter((message) => message.customType === 'V1.IMG')
          .map((image) => {
            return {
              src: image.plainUrl,
              thumbnail:
                image.thumbnails && image.thumbnails.length > 0 ? image.thumbnails[0].plainUrl : '', // Check for array length
            }
          }),
      )
    }
  }, [messages])

  return (
    <ListWrapper>
      <List
        ref={listForwardRef}
        data-testid="channel-conversation"
        preventYScroll={messageListQuery?.isLoading || carouselImages?.length || overlayActive}
        removePaddingBottom={typing}
      >
        <TopListInfo ref={loadMoreRef}>
          {showGdprMessage && <ChannelGdprMessage type={advertiserType} />}
          {tip && <ChannelTipMessage onTipClick={handleTipOnClick} tip={tip} />}
          {showInfoMessage.value && !messageListQuery.hasNext && <ChannelInfoMessage />}
        </TopListInfo>
        {messageListQuery?.isLoading && (
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        )}
        {messageList}
        {typing && (
          <TypingIndicator typing={typing}>
            <p>{`${recipientName} skriver`}</p>
            <DotIndicator />
            <DotIndicator />
            <DotIndicator />
          </TypingIndicator>
        )}
      </List>
      <Overlay show={overlayActive} />
    </ListWrapper>
  )
}

ChannelView.propTypes = {
  // Messages for selected channel (https://docs.sendbird.com/platform/messages#_4_resource_representation).
  messages: PropTypes.array.isRequired,
  // Element of the scrollable area containing conversation messages.
  listForwardRef: PropTypes.shape({ current: PropTypes.object }),
  // Current user Sendbird ID (user_id: https://docs.sendbird.com/platform/user#_4_resource_representation).
  currentUserId: PropTypes.string.isRequired,
  // State set in an event handler from Sendbird, if recipient is typing (https://docs.sendbird.com/javascript/group_channel_advanced#3_send_typing_indicators_to_other_members).
  typing: PropTypes.bool,
  // State if current device is a mobile.
  isMobile: PropTypes.bool,
  // State if recipient read last message.
  recipientReadLastMessage: PropTypes.bool,
  // Current user selected channel (https://docs.sendbird.com/platform/group_channel#_4_resource_representation).
  selectedChannel: PropTypes.object.isRequired,
  // 3rd party advertisement. Response from `messaging/v2/tip` endpoint (https://**YOUR_DEV**-api.dev.blocket.io/swagger/messaging.html).
  tip: PropTypes.object,
  // Load/fetch messages (https://docs.sendbird.com/javascript/group_channel#3_load_previous_messages).
  messageListQuery: PropTypes.object,
  paginateMessages: PropTypes.func,
  recipientName: PropTypes.string,
  // Ads overlay on ad section.
  overlayActive: PropTypes.bool,
  advertiserType: PropTypes.string,
}

export default React.memo(ChannelView)
